import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  BellAlertIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ExclamationTriangleIcon,
  PencilSquareIcon,
  PlusIcon,
  ShieldCheckIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import vehicleMaintenanceApi from '../../api/vehicleMaintenanceApi';
import { sortRowsByVehicleCode, sortVehiclesByVehicleCode } from './internalAuditVehicleCodeSort';
import {
  StatusBadge,
  VehicleModuleHero,
  VehiclePageShell,
  VehiclePlateCell,
  vehiclePlateColumnProps,
  VehicleSelectionPreview,
  VehicleStatCard,
  VehicleStatGrid,
  getVehicleById,
} from './vehicleMaintenanceUi';
import ActionToolbar from '../../components/ui/ActionToolbar';
import DataTable from '../../components/ui/DataTable';
import FilterActions from '../../components/ui/FilterActions';
import FilterPanel from '../../components/ui/FilterPanel';
import IconActionButton from '../../components/ui/IconActionButton';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import ResponsiveModal from '../../components/ui/ResponsiveModal';
import { getVehicleIdFromSearch } from './vehicleQueryParams';

const initialForm = {
  vehicle: '',
  insurance_type: 'traffic',
  policy_number: '',
  provider: '',
  start_date: '',
  expiry_date: '',
  notes: '',
};

const isSyntheticInsuranceRow = (row) => String(row?.id || '').startsWith('sync-');

const deriveStatusFromDate = (isoDate) => {
  if (!isoDate) return 'unknown';
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(target.getTime())) return 'unknown';
  const days = Math.floor((target - today) / (1000 * 60 * 60 * 24));
  if (days < 0) return 'expired';
  if (days <= 30) return 'approaching';
  return 'active';
};

const deriveRemainingDays = (isoDate) => {
  if (!isoDate) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(target.getTime())) return null;
  return Math.floor((target - today) / (1000 * 60 * 60 * 24));
};

function formatKaskoRemainingCell(kaskoRecord) {
  if (!kaskoRecord) return '-';
  const rd = kaskoRecord.remaining_days;
  const n = rd != null && rd !== '' ? Number(rd) : null;
  if (Number.isFinite(n) && n < 0) {
    return <span className="text-[11px] font-medium text-red-300">Kasko günü dolmuştur</span>;
  }
  if (kaskoRecord.follow_up_status === 'expired') {
    return <span className="text-[11px] font-medium text-red-300">Kasko günü dolmuştur</span>;
  }
  return rd ?? '-';
}

const InternalAuditInsurancePage = () => {
  const [rows, setRows] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [formData, setFormData] = useState(initialForm);
  const [filters, setFilters] = useState({ vehicle_id: getVehicleIdFromSearch() });
  const [draftFilters, setDraftFilters] = useState({ vehicle_id: getVehicleIdFromSearch() });
  const [showVehicleDropdown, setShowVehicleDropdown] = useState(false);
  const vehicleDropdownRef = useRef(null);

  const sortedVehicles = useMemo(() => sortVehiclesByVehicleCode(vehicles), [vehicles]);

  const loadData = async (activeFilters = filters) => {
    setLoading(true);
    setError(null);
    try {
      const [insurancesResponse, vehiclesResponse] = await Promise.all([
        vehicleMaintenanceApi.getInsurances(activeFilters),
        vehicleMaintenanceApi.getVehicles({ is_active: 'true' }),
      ]);
      const insuranceRows = insurancesResponse?.data || [];
      const vehicleRows = vehiclesResponse?.data || [];
      const selectedVehicleId = activeFilters?.vehicle_id ? String(activeFilters.vehicle_id) : '';

      const existingKeys = new Set(insuranceRows.map((item) => `${item.vehicle}-${item.insurance_type}`));
      const fallbackRows = vehicleRows
        .filter((vehicle) => !selectedVehicleId || String(vehicle.id) === selectedVehicleId)
        .flatMap((vehicle) => {
          const generated = [];
          if (vehicle.traffic_policy_date && !existingKeys.has(`${vehicle.id}-traffic`)) {
            generated.push({
              id: `sync-${vehicle.id}-traffic`,
              vehicle: vehicle.id,
              vehicle_code: vehicle.vehicle_code,
              license_plate: vehicle.license_plate,
              insurance_type: 'traffic',
              policy_number: null,
              provider: null,
              start_date: null,
              expiry_date: vehicle.traffic_policy_date,
              notes: 'Araç kartından otomatik gösterim',
              follow_up_status: deriveStatusFromDate(vehicle.traffic_policy_date),
              remaining_days: deriveRemainingDays(vehicle.traffic_policy_date),
            });
          }
          if (vehicle.insurance_policy_date && !existingKeys.has(`${vehicle.id}-kasko`)) {
            generated.push({
              id: `sync-${vehicle.id}-kasko`,
              vehicle: vehicle.id,
              vehicle_code: vehicle.vehicle_code,
              license_plate: vehicle.license_plate,
              insurance_type: 'kasko',
              policy_number: null,
              provider: null,
              start_date: null,
              expiry_date: vehicle.insurance_policy_date,
              notes: 'Araç kartından otomatik gösterim',
              follow_up_status: deriveStatusFromDate(vehicle.insurance_policy_date),
              remaining_days: deriveRemainingDays(vehicle.insurance_policy_date),
            });
          }
          return generated;
        });

      setRows([...insuranceRows, ...fallbackRows]);
      setVehicles(vehicleRows);
    } catch (err) {
      setError(err.message || 'Sigorta kayıtları yüklenemedi.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!vehicleDropdownRef.current) return;
      if (!vehicleDropdownRef.current.contains(event.target)) setShowVehicleDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = () => {
    setFilters(draftFilters);
    setShowVehicleDropdown(false);
    loadData(draftFilters);
  };

  const handleClear = () => {
    const reset = { vehicle_id: '' };
    setDraftFilters(reset);
    setFilters(reset);
    setShowVehicleDropdown(false);
    loadData(reset);
  };

  const openCreateModal = () => {
    setEditingRow(null);
    setSelectedRow(null);
    setFormData(initialForm);
    setIsModalOpen(true);
  };

  const openEditModal = (row) => {
    if (!row) return;
    setEditingRow(row);
    setFormData({
      vehicle: row.vehicle || '',
      insurance_type: row.insurance_type || 'traffic',
      policy_number: row.policy_number || '',
      provider: row.provider || '',
      start_date: row.start_date || '',
      expiry_date: row.expiry_date || '',
      notes: row.notes || '',
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (row) => {
    if (!row) return;
    if (!window.confirm('Sigorta kaydını silmek istediğinize emin misiniz?')) return;
    try {
      await vehicleMaintenanceApi.deleteInsurance(row.id);
      setSelectedRow(null);
      await loadData();
    } catch (err) {
      setError(err.message || 'Kayıt silinemedi.');
    }
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      if (editingRow?.id) {
        await vehicleMaintenanceApi.updateInsurance(editingRow.id, formData);
      } else {
        await vehicleMaintenanceApi.createInsurance(formData);
      }
      setIsModalOpen(false);
      setEditingRow(null);
      setSelectedRow(null);
      setFormData(initialForm);
      await loadData();
    } catch (err) {
      setError(err.message || 'Kayıt kaydedilemedi.');
    } finally {
      setSaving(false);
    }
  };

  const groupedRows = useMemo(() => {
    const map = new Map();
    rows.forEach((item) => {
      const key = String(item.vehicle);
      if (!map.has(key)) {
        map.set(key, {
          id: `vehicle-${key}`,
          vehicle: item.vehicle,
          vehicle_code: item.vehicle_code,
          license_plate: item.license_plate,
          traffic_record: null,
          kasko_record: null,
        });
      }
      const group = map.get(key);
      if (item.insurance_type === 'traffic') group.traffic_record = item;
      if (item.insurance_type === 'kasko') group.kasko_record = item;
    });
    return Array.from(map.values());
  }, [rows]);

  const actionableRecord = selectedRow?.traffic_record || selectedRow?.kasko_record || null;

  const columns = useMemo(() => [
    {
      header: 'Araç ID',
      accessorKey: 'vehicle_code',
      cell: ({ getValue }) => (
        <span className="rounded-md bg-slate-100 px-1.5 py-0.5 font-mono text-[11px] font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-200">
          {getValue() || '—'}
        </span>
      ),
    },
    {
      ...vehiclePlateColumnProps,
      cell: ({ row }) => {
        const vehicle = getVehicleById(vehicles, row.original.vehicle);
        return (
          <VehiclePlateCell
            plate={row.original.license_plate}
            brand={vehicle?.brand}
            model={vehicle?.model}
          />
        );
      },
    },
    { header: 'Trafik Bitiş', id: 'traffic_expiry', cell: ({ row }) => row.original.traffic_record?.expiry_date || '—' },
    { header: 'Trafik Kalan Gün', id: 'traffic_remaining', cell: ({ row }) => row.original.traffic_record?.remaining_days ?? '—' },
    { header: 'Kasko Bitiş', id: 'kasko_expiry', cell: ({ row }) => row.original.kasko_record?.expiry_date || '—' },
    { header: 'Kasko Kalan Gün', id: 'kasko_remaining', cell: ({ row }) => formatKaskoRemainingCell(row.original.kasko_record) },
    {
      header: 'Trafik Takip',
      id: 'traffic_status',
      cell: ({ row }) => <StatusBadge status={row.original.traffic_record?.follow_up_status} />,
    },
    {
      header: 'Kasko Takip',
      id: 'kasko_status',
      cell: ({ row }) => <StatusBadge status={row.original.kasko_record?.follow_up_status} />,
    },
  ], [vehicles]);

  const sortedRows = useMemo(() => sortRowsByVehicleCode(groupedRows), [groupedRows]);

  const stats = useMemo(() => {
    const total = sortedRows.length;
    const trafficExpired = sortedRows.filter((r) => r.traffic_record?.follow_up_status === 'expired').length;
    const kaskoExpired = sortedRows.filter((r) => r.kasko_record?.follow_up_status === 'expired').length;
    const approaching = sortedRows.filter(
      (r) =>
        r.traffic_record?.follow_up_status === 'approaching' ||
        r.kasko_record?.follow_up_status === 'approaching'
    ).length;
    const activeOk = sortedRows.filter((r) => {
      const t = r.traffic_record?.follow_up_status;
      const k = r.kasko_record?.follow_up_status;
      const trafficOk = !t || t === 'active';
      const kaskoOk = !k || k === 'active';
      return trafficOk && kaskoOk && (t || k);
    }).length;
    return { total, trafficExpired, kaskoExpired, approaching, activeOk };
  }, [sortedRows]);

  const selectedVehiclePreview = useMemo(() => {
    if (!selectedRow) return null;
    return getVehicleById(vehicles, selectedRow.vehicle) || {
      license_plate: selectedRow.license_plate,
      vehicle_code: selectedRow.vehicle_code,
      brand: null,
      model: null,
      assigned_person: null,
    };
  }, [selectedRow, vehicles]);

  const getRowClassName = (row) => {
    if (
      row.traffic_record?.follow_up_status === 'expired' ||
      row.kasko_record?.follow_up_status === 'expired'
    ) {
      return 'border-l-[3px] border-l-rose-400 dark:border-l-rose-500';
    }
    if (
      row.traffic_record?.follow_up_status === 'approaching' ||
      row.kasko_record?.follow_up_status === 'approaching'
    ) {
      return 'border-l-[3px] border-l-amber-400 dark:border-l-amber-500';
    }
    return '';
  };

  return (
    <VehiclePageShell>
        <VehicleModuleHero
          icon={ShieldCheckIcon}
          title="Trafik - Kasko Takip"
          subtitle="Trafik ve kasko poliçe kayıtlarını yönetin."
          accent="teal"
        />

        <VehicleStatGrid>
          <VehicleStatCard label="Toplam Araç" value={stats.total} tone="slate" icon={ShieldCheckIcon} />
          <VehicleStatCard label="Takibi Tamam" value={stats.activeOk} tone="emerald" icon={CheckCircleIcon} />
          <VehicleStatCard label="Süre Yaklaşıyor" value={stats.approaching} tone="amber" icon={BellAlertIcon} />
          <VehicleStatCard
            label="Süresi Dolan"
            value={stats.trafficExpired + stats.kaskoExpired}
            tone="rose"
            icon={ExclamationTriangleIcon}
          />
        </VehicleStatGrid>

        {selectedVehiclePreview ? (
          <VehicleSelectionPreview vehicle={selectedVehiclePreview} personName={selectedVehiclePreview.assigned_person} />
        ) : null}

        {error ? (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
            <div className="flex items-center justify-between gap-3">
              <span>{error}</span>
              <button type="button" onClick={() => setError(null)} className="text-red-500 hover:text-red-700">
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        ) : null}

        <FilterPanel compact title="Filtreler" isOpen={isFilterOpen} onToggle={() => setIsFilterOpen((prev) => !prev)}>
          <div className="grid grid-cols-1 gap-1.5">
            <div className="rounded-md border border-teal-100/80 bg-gradient-to-br from-white to-teal-50/40 p-1.5 dark:border-teal-900/40 dark:from-gray-800 dark:to-teal-950/20">
              <div className="sr-only text-teal-800 dark:text-teal-300">
                Araç Filtresi
              </div>
              <div className="grid grid-cols-2 gap-1 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-7">
                <fieldset className="rounded border border-gray-200 bg-white px-1 pb-0 pt-0 dark:border-gray-600 dark:bg-gray-700">
                  <legend className="px-0.5 text-[8px] text-gray-500 dark:text-gray-400 leading-none">Araç</legend>
                  <div className="relative" ref={vehicleDropdownRef}>
                    <button
                      type="button"
                      onClick={() => setShowVehicleDropdown((prev) => !prev)}
                      className="flex h-5 w-full items-center justify-between bg-transparent px-0 text-[10px] text-gray-900 outline-none dark:text-white"
                    >
                      <span className="truncate">
                        {(() => {
                          if (!draftFilters.vehicle_id) return 'Tümü';
                          const selected = sortedVehicles.find((v) => String(v.id) === String(draftFilters.vehicle_id));
                          return selected ? `${selected.vehicle_code} - ${selected.license_plate}` : 'Tümü';
                        })()}
                      </span>
                      <ChevronDownIcon className="ml-2 h-4 w-4 shrink-0 text-gray-400" />
                    </button>
                    {showVehicleDropdown ? (
                      <div className="absolute left-0 right-0 z-50 mt-1 max-h-[196px] overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg dark:border-gray-600 dark:bg-gray-800">
                        <button
                          type="button"
                          onClick={() => {
                            setDraftFilters((prev) => ({ ...prev, vehicle_id: '' }));
                            setShowVehicleDropdown(false);
                          }}
                          className="w-full px-2 py-1.5 text-left text-[11px] text-gray-900 hover:bg-gray-100 dark:text-white dark:hover:bg-gray-700"
                        >
                          Tümü
                        </button>
                        {sortedVehicles.map((vehicle) => (
                          <button
                            key={vehicle.id}
                            type="button"
                            onClick={() => {
                              setDraftFilters((prev) => ({ ...prev, vehicle_id: String(vehicle.id) }));
                              setShowVehicleDropdown(false);
                            }}
                            className="w-full px-2 py-1.5 text-left text-[11px] text-gray-900 hover:bg-gray-100 dark:text-white dark:hover:bg-gray-700"
                          >
                            {vehicle.vehicle_code} - {vehicle.license_plate}
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                </fieldset>
              </div>
            </div>
          </div>
          <div className="mt-1.5 flex justify-end border-t border-gray-100 pt-1.5 dark:border-gray-700">
            <FilterActions compact onClear={handleClear} onSearch={handleSearch} />
          </div>
        </FilterPanel>

        <ActionToolbar className="rounded-xl border border-teal-100/80 bg-white/80 px-2 py-1.5 shadow-sm dark:border-teal-900/40 dark:bg-gray-900/60">
          <IconActionButton icon={PlusIcon} onClick={openCreateModal} title="Yeni Kayıt" />
          <IconActionButton icon={PencilSquareIcon} onClick={() => openEditModal(actionableRecord)} title="Düzenle" disabled={!actionableRecord || isSyntheticInsuranceRow(actionableRecord)} />
          <IconActionButton icon={TrashIcon} onClick={() => handleDelete(actionableRecord)} title="Sil" disabled={!actionableRecord || isSyntheticInsuranceRow(actionableRecord)} />
        </ActionToolbar>

        <DataTable
          data={sortedRows}
          columns={columns}
          isLoading={loading}
          showSearch={false}
          onRowClick={setSelectedRow}
          onRowDoubleClick={(row) => {
            const record = row?.traffic_record || row?.kasko_record;
            if (record && !isSyntheticInsuranceRow(record)) openEditModal(record);
          }}
          selectedRowId={selectedRow?.id}
          compact
          compactCellWrap
          modern
          getRowClassName={getRowClassName}
        />

      <ResponsiveModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} className="max-w-3xl">
        <div className="px-4 pt-5 pb-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-gray-900 dark:text-white">
              {editingRow ? 'Sigorta Kaydını Düzenle' : 'Yeni Sigorta Kaydı'}
            </h2>
            <button type="button" onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300">
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSave}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-6">
              <div className="space-y-1">
                <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">Araç</Label>
                <select required className="flex h-11 md:h-10 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white" value={formData.vehicle} onChange={(e) => setFormData((prev) => ({ ...prev, vehicle: e.target.value }))}>
                  <option value="">Araç seçin</option>
                  {sortedVehicles.map((vehicle) => (
                    <option key={vehicle.id} value={vehicle.id}>
                      {vehicle.vehicle_code} - {vehicle.license_plate}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">Sigorta Tipi</Label>
                <select className="flex h-11 md:h-10 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white" value={formData.insurance_type} onChange={(e) => setFormData((prev) => ({ ...prev, insurance_type: e.target.value }))}>
                  <option value="traffic">Trafik</option>
                  <option value="kasko">Kasko</option>
                </select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">Poliçe No</Label>
                <Input value={formData.policy_number} onChange={(e) => setFormData((prev) => ({ ...prev, policy_number: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">Sağlayıcı</Label>
                <Input value={formData.provider} onChange={(e) => setFormData((prev) => ({ ...prev, provider: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">Başlangıç Tarihi</Label>
                <Input type="date" value={formData.start_date} onChange={(e) => setFormData((prev) => ({ ...prev, start_date: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">Bitiş Tarihi</Label>
                <Input required type="date" value={formData.expiry_date} onChange={(e) => setFormData((prev) => ({ ...prev, expiry_date: e.target.value }))} />
              </div>
              <div className="space-y-1 md:col-span-2">
                <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">Notlar</Label>
                <Input value={formData.notes} onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))} />
              </div>
            </div>
            <div className="mt-6 flex items-center justify-end space-x-3">
              <Button variant="outline" type="button" onClick={() => setIsModalOpen(false)}>İptal</Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white" disabled={saving}>{saving ? 'Kaydediliyor...' : 'Kaydet'}</Button>
            </div>
          </form>
        </div>
      </ResponsiveModal>
    </VehiclePageShell>
  );
};

export default InternalAuditInsurancePage;
